import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreatePlanUpgradeRequestInput,
  MyPlanUpgradeRequestsResult,
  PlanUpgradeRequest,
} from '@terafile/shared'
import { type FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { useMeTenant } from '../lib/me-api.js'
import {
  MY_PLAN_UPGRADE_REQUESTS_QUERY_KEY,
  cancelPlanUpgradeRequest,
  createPlanUpgradeRequest,
  listMyPlanUpgradeRequests,
  updatePlanUpgradeRequest,
} from '../lib/plan-upgrade-api.js'
import { useToast } from '../lib/toast.js'

const EMPTY_FORM = {
  contactName: '',
  companyName: '',
  phone: '',
  postalCode: '',
  billingAddress: '',
  billingEmail: '',
  desiredAddon: 0,
  message: '',
}

/**
 * 有料プランアップグレード申請フォーム。
 *
 * - 有効な申請がある間はフォーム非表示（履歴から編集/キャンセル）
 * - キャンセル / 見送り後は新規申込可能
 */
export function PlanUpgradePage() {
  const meTenantQ = useMeTenant()
  const queryClient = useQueryClient()
  const toast = useToast()

  const myRequestsQ = useQuery({
    queryKey: MY_PLAN_UPGRADE_REQUESTS_QUERY_KEY,
    queryFn: listMyPlanUpgradeRequests,
    enabled: meTenantQ.data?.kind === 'REGULAR',
    placeholderData: (prev) => prev,
  })

  const [form, setForm] = useState(EMPTY_FORM)
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const items = myRequestsQ.data?.items ?? []
  const activeRequest = items.find((r) => r.status === 'PENDING' || r.status === 'CONTACTED')
  const canCreate = !activeRequest
  const isEditMode = editingRequestId !== null

  useEffect(() => {
    if (editingRequestId) {
      const request = items.find((r) => r.id === editingRequestId)
      if (request) {
        setForm(requestToFormState(request))
      }
      return
    }
    if (canCreate) {
      setForm(EMPTY_FORM)
    }
  }, [canCreate, editingRequestId, items])

  const syncRequestsCache = (updater: (items: PlanUpgradeRequest[]) => PlanUpgradeRequest[]) => {
    queryClient.setQueryData<MyPlanUpgradeRequestsResult>(
      MY_PLAN_UPGRADE_REQUESTS_QUERY_KEY,
      (prev) => ({
        items: updater(prev?.items ?? []),
      }),
    )
  }

  const createMut = useMutation({
    mutationFn: createPlanUpgradeRequest,
    onSuccess: (created) => {
      syncRequestsCache((prevItems) => [
        created,
        ...prevItems.filter((item) => item.id !== created.id),
      ])
      toast.success('申請を受け付けました。担当者から連絡いたします')
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreatePlanUpgradeRequestInput }) =>
      updatePlanUpgradeRequest(id, input),
    onSuccess: (updated) => {
      syncRequestsCache((prevItems) =>
        prevItems.map((item) => (item.id === updated.id ? updated : item)),
      )
      setEditingRequestId(null)
      toast.success('申請内容を更新しました')
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const cancelMut = useMutation({
    mutationFn: cancelPlanUpgradeRequest,
    onSuccess: (cancelled) => {
      syncRequestsCache((prevItems) =>
        prevItems.map((item) => (item.id === cancelled.id ? cancelled : item)),
      )
      setEditingRequestId(null)
      toast.success('申請をキャンセルしました')
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  const isSubmitting = createMut.isPending || updateMut.isPending || cancelMut.isPending

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const input = buildFormInput(form)
    if (editingRequestId) {
      updateMut.mutate({ id: editingRequestId, input })
      return
    }
    if (canCreate) {
      createMut.mutate(input)
    }
  }

  const onEditRequest = (request: PlanUpgradeRequest) => {
    setError(null)
    setEditingRequestId(request.id)
    setForm(requestToFormState(request))
  }

  const onCancelRequest = (requestId: string) => {
    if (!window.confirm('申請をキャンセルしますか？キャンセル後は新規申込みが可能になります。')) {
      return
    }
    setError(null)
    cancelMut.mutate(requestId)
  }

  const isEphemeral = meTenantQ.data?.kind === 'EPHEMERAL'
  const isAlreadyPro = meTenantQ.data?.plan === 'PRO'
  const isRegular = meTenantQ.data?.kind === 'REGULAR'
  const requestsReady = !isRegular || myRequestsQ.data !== undefined || myRequestsQ.isError
  const showForm = !isEphemeral && !isAlreadyPro && requestsReady && (canCreate || isEditMode)
  const showHistory = !isEphemeral && !isAlreadyPro
  const historyLoading = myRequestsQ.isLoading && myRequestsQ.data === undefined

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16 space-y-12 grain">
      <section>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground/80 mb-3">
          Upgrade · PRO
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-semibold leading-[1.05] tracking-tight">
          有料プラン申し込み
        </h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-prose">
          有料プラン (PRO) ではプロジェクト数無制限・容量
          <span className="font-medium text-foreground"> 2TB </span>
          から（追加ストレージ +1TB ごとに拡張可）。お申込み後、担当者から折り返しご連絡いたします。
        </p>
      </section>

      {isEphemeral && (
        <div className="border border-amber-300 bg-amber-50 rounded-md p-4 text-sm text-amber-900">
          お試し中は申請できません。先に{' '}
          <Link to="/signup" className="underline font-medium">
            アカウント登録
          </Link>{' '}
          してから申請してください。
        </div>
      )}

      {isAlreadyPro && (
        <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 text-sm text-emerald-900">
          既に有料プランをご利用中です。追加ストレージなどのご相談は管理者までご連絡ください。
        </div>
      )}

      {showHistory && (
        <section className="space-y-3">
          <h2 className="font-display text-xl font-medium tracking-tight">申請履歴</h2>
          {historyLoading ? (
            <p className="text-sm text-muted-foreground py-4">読み込み中…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 border-y border-border">
              申請履歴はまだありません。
            </p>
          ) : (
            <ul className="divide-y divide-border border-y border-border">
              {items.map((r) => (
                <RequestRow
                  key={r.id}
                  request={r}
                  isEditing={editingRequestId === r.id}
                  isCancelPending={cancelMut.isPending && cancelMut.variables === r.id}
                  onEdit={() => onEditRequest(r)}
                  onCancel={() => onCancelRequest(r.id)}
                />
              ))}
            </ul>
          )}
        </section>
      )}

      {showForm && (
        <section className="border border-border rounded-lg p-6 space-y-5">
          {isEditMode && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-md p-3 flex-1">
                申請内容を編集しています。保存後は履歴に戻ります。
              </p>
              <button
                type="button"
                onClick={() => {
                  setEditingRequestId(null)
                  setError(null)
                }}
                className="text-xs text-muted-foreground hover:text-foreground shrink-0"
              >
                閉じる
              </button>
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="担当者名" required>
              <input
                type="text"
                value={form.contactName}
                onChange={(e) => setForm((prev) => ({ ...prev, contactName: e.target.value }))}
                maxLength={120}
                required
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="例: テラファイル太郎"
                disabled={isSubmitting}
              />
            </Field>
            <Field label="会社名" required>
              <input
                type="text"
                value={form.companyName}
                onChange={(e) => setForm((prev) => ({ ...prev, companyName: e.target.value }))}
                maxLength={200}
                required
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="例: テラファイル便"
                disabled={isSubmitting}
              />
            </Field>
            <Field label="電話番号" required>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                maxLength={50}
                required
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="例: 03-1234-5678"
                disabled={isSubmitting}
              />
            </Field>

            <SectionHeader>請求書送付先</SectionHeader>

            <Field label="郵便番号" required hint="数字とハイフンのみ。例: 100-0001 / 1000001">
              <input
                type="text"
                value={form.postalCode}
                onChange={(e) => setForm((prev) => ({ ...prev, postalCode: e.target.value }))}
                maxLength={20}
                inputMode="numeric"
                pattern="[0-9-]+"
                required
                className="w-40 px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="100-0001"
                disabled={isSubmitting}
              />
            </Field>
            <Field
              label="住所"
              required
              hint="都道府県・市区町村・番地・建物名を1つにまとめて入力してください"
            >
              <input
                type="text"
                value={form.billingAddress}
                onChange={(e) => setForm((prev) => ({ ...prev, billingAddress: e.target.value }))}
                maxLength={500}
                required
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="例: 東京都千代田区千代田 1-1 ◯◯ビル 5F"
                disabled={isSubmitting}
              />
            </Field>
            <Field
              label="請求書送付先メールアドレス"
              hint="未入力の場合は申請者のメールアドレスへ送信します"
            >
              <input
                type="email"
                value={form.billingEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, billingEmail: e.target.value }))}
                maxLength={255}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="例: invoice@example.com"
                disabled={isSubmitting}
              />
            </Field>

            <SectionHeader>希望プラン</SectionHeader>

            <Field
              label="追加ストレージ希望 (TB)"
              hint="基本 2TB に加算します。0 の場合は基本のみ。"
            >
              <input
                type="number"
                min={0}
                max={1000}
                value={form.desiredAddon}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    desiredAddon: Math.max(0, Number(e.target.value) || 0),
                  }))
                }
                className="w-32 px-3 py-2 border border-border rounded-md text-sm bg-background"
                disabled={isSubmitting}
              />
            </Field>
            <Field label="ご要望・メッセージ">
              <textarea
                value={form.message}
                onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                maxLength={2000}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background"
                placeholder="想定利用内容・希望開始時期・気になる点など"
                disabled={isSubmitting}
              />
            </Field>

            {error && (
              <p className="text-xs text-destructive bg-destructive/5 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                {createMut.isPending || updateMut.isPending
                  ? '送信中…'
                  : isEditMode
                    ? '更新する'
                    : '申し込む'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  )
}

function requestToFormState(request: PlanUpgradeRequest) {
  return {
    contactName: request.contactName ?? '',
    companyName: request.companyName ?? '',
    phone: request.phone ?? '',
    postalCode: request.postalCode ?? '',
    billingAddress: request.billingAddress ?? '',
    billingEmail: request.billingEmail ?? '',
    desiredAddon: request.desiredAddonStorageTb,
    message: request.message ?? '',
  }
}

function buildFormInput(form: typeof EMPTY_FORM): CreatePlanUpgradeRequestInput {
  return {
    contactName: form.contactName.trim(),
    companyName: form.companyName.trim(),
    phone: form.phone.trim(),
    postalCode: form.postalCode.trim(),
    billingAddress: form.billingAddress.trim(),
    ...(form.billingEmail.trim() ? { billingEmail: form.billingEmail.trim() } : {}),
    desiredAddonStorageTb: form.desiredAddon,
    ...(form.message.trim() ? { message: form.message.trim() } : {}),
  }
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: children として input 要素を内包するパターン
    <label className="block">
      <span className="text-sm font-medium block mb-1.5">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </span>
      {children}
      {hint && <span className="text-xs text-muted-foreground mt-1 block">{hint}</span>}
    </label>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 pt-3 border-t border-border">
      {children}
    </h3>
  )
}

const STATUS_LABEL: Record<PlanUpgradeRequest['status'], { label: string; className: string }> = {
  PENDING: {
    label: '受付済み',
    className: 'border-amber-300 text-amber-900 bg-amber-50',
  },
  CONTACTED: {
    label: '対応中',
    className: 'border-sky-300 text-sky-900 bg-sky-50',
  },
  APPROVED: {
    label: '承認',
    className: 'border-emerald-300 text-emerald-900 bg-emerald-50',
  },
  REJECTED: {
    label: '見送り',
    className: 'border-destructive/30 text-destructive bg-destructive/5',
  },
  CANCELLED: {
    label: 'キャンセル',
    className: 'border-border text-muted-foreground bg-muted/30',
  },
}

function RequestRow({
  request,
  isEditing,
  isCancelPending,
  onEdit,
  onCancel,
}: {
  request: PlanUpgradeRequest
  isEditing: boolean
  isCancelPending: boolean
  onEdit: () => void
  onCancel: () => void
}) {
  const conf = STATUS_LABEL[request.status]
  const summary = [request.contactName, request.companyName].filter(Boolean).join(' · ')
  return (
    <li className="py-4 flex items-center gap-3 flex-wrap">
      <span
        className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-full font-medium ${conf.className}`}
      >
        {conf.label}
      </span>
      <span className="text-xs text-muted-foreground font-mono editorial-num">
        {new Date(request.createdAt).toLocaleString('ja-JP')}
      </span>
      {summary && <span className="text-xs text-foreground">{summary}</span>}
      <span className="text-xs text-muted-foreground">+{request.desiredAddonStorageTb} TB</span>
      {request.reviewerNote && (
        <span className="text-xs text-muted-foreground italic flex-1 min-w-0 truncate">
          管理者メモ: {request.reviewerNote}
        </span>
      )}
      {request.status === 'PENDING' && (
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={onEdit}
            disabled={isEditing || isCancelPending}
            className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted/50 transition-colors disabled:opacity-40"
          >
            {isEditing ? '編集中' : '編集'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isCancelPending}
            className="text-xs px-3 py-1.5 border border-border rounded-md hover:bg-muted/50 transition-colors disabled:opacity-40"
          >
            {isCancelPending ? 'キャンセル中…' : 'キャンセル'}
          </button>
        </div>
      )}
    </li>
  )
}
